import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { deleteData, getData } from '../../../DataAccessLayer';
import usePollWhilePending from './usePollWhilePending';
import { isResourcePending } from './resourceStatus';

/**
 * Orders a list of resources newest first, which is how both views show them.
 * @param {Array<Object>} resources The resources as the server returned them
 * @returns {Array<Object>} The same resources, most recently created first
 */
const byNewestFirst = (resources) =>
  [...resources].sort((a, b) => new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0));

/**
 * Loads one kind of generated resource and runs the delete-with-confirmation
 * flow over it. The desktop table and the phone list share this so the two
 * cannot drift apart. Keeps polling in the background for as long as a row
 * is still generating, so a resource's status updates without a page reload.
 *
 * @param {Object} resourceType One entry from RESOURCE_TYPES
 * @returns {Object} The list, its loading and error state, and the delete flow
 */
export const useResources = ({ service, label, lowercasePlural }) => {
  const courseId = useSelector((state) => state.course.courseId);
  const impersonatedUserId = useSelector((state) => state.impersonation.impersonation?.midas);
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchResources = useCallback(async () => {
    const data = (await getData(service({ courseId }))) ?? [];
    setResources(data);
    return data;
    // impersonatedUserId isn't part of the request itself (the dev portal
    // sends it as a header), but switching who the request is made as must
    // reload this list the same way a courseId change does.
    // So, we need to pass impersonatedUserId in the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, courseId, impersonatedUserId]);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      await fetchResources();
    } catch (err) {
      console.log(err);
      setLoadError(
        typeof err === 'string' ? err : (err?.message ?? `Unable to load ${lowercasePlural}.`),
      );
    } finally {
      setIsLoading(false);
    }
  }, [fetchResources, lowercasePlural]);

  useEffect(() => {
    reload();
  }, [reload]);

  /*
   * The server returns these in no particular order. Sorting here rather than in
   * either view is what keeps the phone list and the desktop table agreeing on
   * what the newest quiz is; the table sorts by the same column on top of this,
   * so its own column headers still work.
   */
  const sortedResources = useMemo(() => byNewestFirst(resources), [resources]);

  usePollWhilePending({
    poll: fetchResources,
    isPending: isResourcePending,
    enabled: resources.some(isResourcePending),
  });

  const confirmDelete = async () => {
    const resource = resourceToDelete;
    setResourceToDelete(null);
    setIsDeleting(true);
    try {
      await deleteData(service({ id: resource.id, courseId }));
      await reload();
      toast.success(`${label} deleted successfully.`);
    } catch (err) {
      console.log(err);
      toast.error(`Failed to delete ${label.toLowerCase()}. Please try again.`);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    resources: sortedResources,
    isLoading,
    loadError,
    reload,
    isDeleting,
    resourceToDelete,
    requestDelete: setResourceToDelete,
    cancelDelete: () => setResourceToDelete(null),
    confirmDelete,
  };
};
